import { useCallback, useMemo, useRef, useState } from "react";
import {
	Box,
	CircularProgress,
	GlobalStyles,
	Paper,
	Typography,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridApi, GridReadyEvent } from "ag-grid-community";
import { ErrorModal } from "@dexteel/mesf-core";
import moment from "moment";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";

import { BilletsBetweenCobblesToolbar } from "./BilletsBetweenCobblesToolbar";
import { BilletsBetweenCobblesSummary } from "./BilletsBetweenCobblesSummary";
import {
	useBilletsBetweenCobblesData,
	useBilletsBetweenCobblesPickers,
} from "../hooks/useBilletsBetweenCobbles";
import {
	FALLBACK_REPORT_TYPES,
	REPORT_TYPE_BY_SHIFT_SIZE,
	type BilletsBetweenCobblesRow,
} from "../models/BilletsBetweenCobbles";

interface Filters {
	crewId: string | null;
	productTypeId: number | null;
	reportType: number;
}

const CENTER_CELL = { textAlign: "center" } as const;
const CENTER_HEADER = "ag-cell-center-header";

const buildColumnDefs = (): ColDef<BilletsBetweenCobblesRow>[] => [
	{
		field: "ProductionDate",
		headerName: "Production Date",
		width: 140,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Shift",
		headerName: "Shift",
		width: 90,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Crew",
		headerName: "Crew",
		width: 90,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "ProductType",
		headerName: "Product Type",
		width: 150,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "ProductSize",
		headerName: "Product",
		flex: 1,
		minWidth: 150,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "TotalBilletsCount",
		headerName: "Total Billets Count",
		width: 150,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => (p.value != null ? String(p.value) : ""),
	},
	{
		field: "CobbleCount",
		headerName: "Cobble Count",
		width: 130,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => (p.value != null ? String(p.value) : ""),
	},
	{
		field: "BBC",
		headerName: "Average BBC",
		width: 130,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => (p.value != null ? String(p.value) : ""),
	},
];

// Default range mirrors the legacy IHBox frontend (billets-between-cobbles.js:30-31):
//   start = today-2, end = today  →  3-day window
// KPI fact tables lag a couple of days behind real-time, so "today/today"
// almost always returns 0 rows. The 3-day window matches what users see
// in the legacy page out of the box.
const URL_SCHEMA = {
	start: urlParam.date(() => moment().startOf("day").subtract(2, "days")),
	end: urlParam.date(() => moment().startOf("day")),
	crewId: urlParam.string(),
	productTypeId: urlParam.int(),
	reportTypeId: urlParam.int(REPORT_TYPE_BY_SHIFT_SIZE),
};

export const BilletsBetweenCobblesReport = () => {
	const [{ start, end, crewId, productTypeId, reportTypeId }, setUrlFilters] =
		useUrlState(URL_SCHEMA);
	const filters: Filters = { crewId, productTypeId, reportType: reportTypeId };
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<BilletsBetweenCobblesRow> | null>(null);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const pickersQuery = useBilletsBetweenCobblesPickers({
		start: startStr,
		end: endStr,
	});
	const dataQuery = useBilletsBetweenCobblesData({
		start: startStr,
		end: endStr,
		crewId: filters.crewId,
		productTypeId: filters.productTypeId,
		reportType: filters.reportType,
		sortType: null,
	});

	const columnDefs = useMemo(() => buildColumnDefs(), []);
	const rows = useMemo(() => dataQuery.data ?? [], [dataQuery.data]);
	const reportTypes = pickersQuery.data?.reportTypes?.length
		? pickersQuery.data.reportTypes
		: FALLBACK_REPORT_TYPES;

	const handleGridReady = useCallback(
		(event: GridReadyEvent<BilletsBetweenCobblesRow>) => {
			gridApiRef.current = event.api;
		},
		[],
	);

	const handleStartChange = useCallback(
		(d: moment.Moment | null) => {
			if (isUsableDate(d)) setUrlFilters({ start: d });
		},
		[setUrlFilters],
	);

	const handleEndChange = useCallback(
		(d: moment.Moment | null) => {
			if (isUsableDate(d)) setUrlFilters({ end: d });
		},
		[setUrlFilters],
	);

	const handleRefresh = useCallback(() => {
		pickersQuery.refetch();
		dataQuery.refetch();
	}, [pickersQuery, dataQuery]);

	const handleExportToExcel = useCallback(() => {
		if (!gridApiRef.current) return;
		gridApiRef.current.exportDataAsExcel({
			fileName: "Billets_Between_Cobbles.xlsx",
			columnKeys: [
				"ProductionDate",
				"Shift",
				"Crew",
				"ProductType",
				"ProductSize",
				"TotalBilletsCount",
				"CobbleCount",
				"BBC",
			],
		});
	}, []);

	const isFetching = pickersQuery.isFetching || dataQuery.isFetching;
	const isLoading = dataQuery.isLoading;

	if (pickersQuery.error && !errorMsg) {
		setErrorMsg((pickersQuery.error as Error).message);
	}
	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Billets Between Cobbles
			</Typography>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<Paper elevation={3}>
				<BilletsBetweenCobblesToolbar
					start={start}
					end={end}
					onStartChange={handleStartChange}
					onEndChange={handleEndChange}
					productTypeId={filters.productTypeId}
					crewId={filters.crewId}
					reportType={filters.reportType}
					onProductTypeChange={(id) => setUrlFilters({ productTypeId: id })}
					onCrewChange={(id) => setUrlFilters({ crewId: id })}
					onReportTypeChange={(id) => setUrlFilters({ reportTypeId: id })}
					productTypes={pickersQuery.data?.productTypes ?? []}
					crews={pickersQuery.data?.crews ?? []}
					reportTypes={reportTypes}
					onRefresh={handleRefresh}
					onExportExcel={handleExportToExcel}
					isLoading={isFetching}
				/>
			</Paper>

			<BilletsBetweenCobblesSummary rows={rows} />

			<Paper elevation={3} sx={{ flex: 1, overflow: "hidden" }}>
				{isLoading ? (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
						}}
					>
						<CircularProgress />
					</Box>
				) : (
					<AgGridReact<BilletsBetweenCobblesRow>
						columnDefs={columnDefs}
						rowData={rows}
						rowModelType="clientSide"
						pagination={false}
						defaultColDef={{ resizable: true }}
						autoSizeStrategy={{ type: "fitGridWidth" }}
						suppressContextMenu
						headerHeight={32}
						rowHeight={28}
						onGridReady={handleGridReady}
					/>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
